<template lang="pug">
#events-tab
  spinner(v-if="settings.loading")
  b-notification(
    v-else-if="events.length === 0"
    :closable="false"
  )
    .has-text-centered No {{ type }}s
  b-table(
    v-else
    sort-icon="chevron-up"
    :backend-sorting="type === 'event'"
    :data="events",
    :default-sort="[filters.sort.field, filters.sort.order]"
    :default-sort-direction="filters.sort.order"
    :draggable="type === 'project'"
    :current-page="pagination.page"
    :per-page="pagination.per"
    :row-class="rowClass"
    :total="pagination.total"
    @click="open"
    @dragstart="dragstart"
    @dragover="dragover"
    @dragleave="dragleave"
    @drop="drop"
    @page-change="getPage"
    @sort="sort"
    backend-pagination
    hoverable
    paginated
  )
    template(slot-scope="props")
      b-table-column(
        width="25"
        :visible="props.row.type === 'project'"
      )
        b-icon.drag-icon.has-text-grey-light(icon="drag-vertical")
      b-table-column(
        label="Title"
        field="title.raw"
        :sortable="type === 'event'"
      )
        b-icon(
          icon="circle"
          size="is-small"
        )
        span {{ props.row.title | truncate(30) }}
      b-table-column(
        label="Publish Date"
        field="pub_date"
        :sortable="type === 'event'"
      )
        small(v-if="props.row.is_published") {{ props.row.pub_date | date }}
        small.has-text-grey-light(v-else) Draft
      b-table-column(
        label="Run Through"
        field="end_date"
        :visible="props.row.type === 'event'"
        :sortable="type === 'event'"
      )
        small(v-if="props.row.is_published") {{ props.row.end_date | date }}
        small.has-text-grey-light(v-else) NA
      b-table-column(
        label="Sign Ups"
        field="registration_count"
        :sortable="type === 'event'"
      )
        small(v-if="props.row.registration_required") {{ props.row.registration_count }}
        small.has-text-grey-light(v-else) NA
      b-table-column(
        label="Fees Collected"
        field="total_amount"
        :sortable="type === 'event'"
      )
        small(v-if="props.row.payment_required") {{ props.row.total_amount | cents }}
        small.has-text-grey-light(v-else) NA
      b-table-column(label="Page")
        small
          a.has-text-primary(:href="`https://${account.host}/events/${props.row.key}`" target="_blank")
            span View
            b-icon(icon="open-in-new" size="is-small")
      b-table-column(label="Manage" width="220")
        .sign-up-actions.level(v-if="props.row.registration_required")
          .level-left
            .level-item
              router-link.button.is-small.is-primary(
                :to="{ name: 'event_registrations', params: { id: props.row.id } }"
                :disabled="props.row.registration_count === 0"
                @click.native.stop=""
                target="_blank"
              )
                b-icon(icon="account-multiple" size="is-small")
                span Sign Ups
            .level-item
              router-link.button.is-small.is-primary(
                :to="{ name: 'event_form', params: { id: props.row.id } }"
                @click.native.stop=""
                target="_blank"
              )
                b-icon(icon="pencil" size="is-small")
                span Form
        small.has-text-grey-light(v-else) NA
</template>

<script>
import { mapGetters } from 'vuex';
import moment from 'moment';
import EventForm from '@/components/form/EventForm';
import Spinner from '@/components/Spinner';
import * as actions from '@/store/types/actions';
import * as getters from '@/store/types/getters';
import * as mutations from '@/store/types/mutations';

export default {
  name: 'events-tab',
  components: {
    Spinner,
  },
  computed: {
    ...mapGetters({
      account: getters.GET_ACCOUNT,
      eventsTags: getters.GET_EVENTS_TAGS,
      editing: getters.GET_EVENT,
      filters: getters.GET_EVENT_FILTERS,
      refresh: getters.GET_EVENTS_REFRESH,
    }),
    esQuery() {
      const from = this.pagination.per * (this.pagination.page - 1);
      const sort = {};
      sort[this.filters.sort.field] = this.filters.sort.order;
      const payload = {
        from,
        size: this.pagination.per,
        sort: [sort],
        query: {
          bool: {
            must: [
              { match: { account_id: this.account.id } },
              { match: { type: this.type } },
            ],
          },
        },
      };
      const today = moment().format('YYYY-MM-DD');
      if (this.type === 'event') {
        switch (this.filters.status) {
          case 'current':
            payload.query.bool.must.push({
              range: { end_date: { gte: today } },
            });
            break;
          case 'past':
            payload.query.bool.must.push({
              range: { end_date: { lt: today } },
            });
            break;
          default:
        }
      }
      switch (this.filters.pub) {
        case 'published':
          payload.query.bool.must.push({
            match: { is_published: true },
          });
          break;
        case 'draft':
          payload.query.bool.must.push({
            match: { is_published: false },
          });
          break;
        default:
      }
      this.eventsTags.forEach((tag) => {
        const item = {};
        switch (tag._index) {
          case 'doxa_events':
            item.match = { id: tag._id };
            break;
          case 'doxa_ministries':
            item.terms = { ministry_ids: [tag._id] };
            break;
          default:
        }
        if (Object.entries(item).length > 0) {
          payload.query.bool.must.push(item);
        }
      });
      return payload;
    },
  },
  created() {
    this.getPage(this.pagination.page);
  },
  data() {
    return {
      dragging: null,
      events: [],
      pagination: {
        page: 1,
        per: 10,
        total: 0,
      },
      settings: {
        loading: false,
        query: '',
        searching: false,
        stage: 'current',
        activeTab: 0,
      },
    };
  },
  methods: {
    dragstart(payload) {
      this.dragging = payload.row;
      payload.event.dataTransfer.effectAllowed = 'copy';
    },
    dragover(payload) {
      payload.event.dataTransfer.dropEffect = 'copy';
      payload.event.target.closest('tr').classList.add('is-selected');
      payload.event.preventDefault();
    },
    dragleave(payload) {
      payload.event.target.closest('tr').classList.remove('is-selected');
      payload.event.preventDefault();
    },
    async drop(payload) {
      payload.event.target.closest('tr').classList.remove('is-selected');
      const index = _.findIndex(this.events, { id: this.dragging.id });
      if (index >= 0) {
        this.events.splice(index, 1);
      }
      this.events.splice(payload.index, 0, this.dragging);
      const saveEvents = [];
      this.events.forEach(event => saveEvents.push(this.saveEvent(event)));
      await Promise.all(saveEvents);
      this.$store.commit(mutations.SET_APP_SNACKBAR, {
        type: 'is-success',
        message: 'Sort order updated',
      });
    },
    async getPage(page, skipAnimation) {
      if (!skipAnimation) this.settings.loading = true;
      this.pagination.page = page;
      // this.$router.push({ name: 'events', query: { page } });
      const payload = Object.assign({ ignore: true }, this.esQuery);
      const response = await this.$store.dispatch(actions.DO_SEARCH_EVENTS, payload);
      this.events = response.hits.hits.map(h => h._source);
      this.pagination.total = response.hits.total;
      if (!skipAnimation) this.settings.loading = false;
      this.$store.commit(mutations.SET_EVENTS_REFRESH, false);
    },
    async open(event) {
      await this.$store.dispatch(actions.DO_GET_EVENT, {
        id: event.id,
        zoom: 'artwork,contact,location,packages',
      });
      this.$buefy.modal.open({
        parent: this,
        component: EventForm,
        hasModalCard: true,
      });
    },
    rowClass(row) {
      if (row.is_published) {
        const today = moment();
        if (today.isAfter(row.end_date, 'day', '[]')) return 'is-past';
        return today.isSameOrAfter(row.pub_date, 'day', '[]') ? 'is-published' : 'is-pending';
      }
      return 'is-draft';
    },
    async saveEvent(event) {
      const index = _.findIndex(this.events, { id: event.id });
      if (index >= 0) {
        await this.$store.dispatch(actions.DO_UPDATE_EVENT, {
          id: event.id,
          sort_order: index,
        });
      }
    },
    sort(field, order) {
      this.$store.commit(mutations.SET_EVENT_FILTER_FIELD, {
        key: 'sort.field',
        val: field,
      });
      this.$store.commit(mutations.SET_EVENT_FILTER_FIELD, {
        key: 'sort.order',
        val: order,
      });
      this.getPage(1, true);
    },
  },
  props: {
    type: {
      type: String,
      default: 'events',
    },
  },
  watch: {
    refresh(val) {
      if (val) this.getPage(1);
    },
  },
};
</script>

<style lang="scss">
#events-tab {
  .notification {
    margin: 0 1rem;
  }
  .table {
    tr {
      .mdi-circle {
        margin-right: 0.5rem;
      }
      &.is-draft {
        .mdi-circle {
          color: $grey-light;
        }
      }
      &.is-published {
        .mdi-circle {
          color: $theme-success;
        }
      }
      &.is-pending {
        .mdi-circle {
          color: $warning;
        }
      }
      &.is-past {
        .mdi-circle {
          color: $danger;
        }
      }
    }
  }
}
</style>



// WEBPACK FOOTER //
// src/components/EventsTab.vue